import { useCallback, useEffect, useRef, useState } from 'react'
import { Conversation } from '@elevenlabs/client'
import { api } from '../lib/api'

// "Talk to Cathy", on the principal's welcome band. One press plays a spoken
// greeting — our sentence, synthesised on the server (POST /cathy/greet) —
// with no microphone involved. Then two choices: ask her a question, which
// asks for the mic and hands the page to her live agent over a signed URL
// (POST /cathy/session), or no thanks. She never introduces herself twice:
// the agent has no first message, the greeting was it.
//
// Phases: idle → greeting → offering → connecting → connected → ended. The
// greeting plays through one Audio element made inside the click handler,
// so autoplay rules are satisfied. Leaving the page pauses the audio and
// ends the session. Hidden until the server says Cathy is configured.

const GREETING = 'welcome'

export default function TalkToCathy({ agentName, name, org, count, variables = {}, hint = '' }) {
  const [available, setAvailable] = useState(null) // null = not asked yet
  const [phase, setPhase] = useState('idle')
  const [speaking, setSpeaking] = useState(false)
  const [error, setError] = useState(null)
  const audioRef = useRef(null)
  const conversationRef = useRef(null)
  const activeRef = useRef(true)

  useEffect(() => {
    activeRef.current = true
    api('/api/v1/cathy/status')
      .then((status) => {
        if (activeRef.current) setAvailable(Boolean(status.available))
      })
      .catch(() => {
        if (activeRef.current) setAvailable(false)
      })
    return () => {
      activeRef.current = false
      // Navigating away never leaves her talking.
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.src = ''
      }
      conversationRef.current?.endSession().catch(() => {})
      conversationRef.current = null
    }
  }, [])

  const greet = useCallback(async () => {
    setError(null)
    setPhase('greeting')
    // One reusable element, created on the press — a user gesture — so the
    // browser lets it play.
    if (!audioRef.current) audioRef.current = new Audio()
    const audio = audioRef.current
    try {
      const response = await fetch('/api/v1/cathy/greet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ line: GREETING, name, org, count }),
      })
      if (!response.ok) {
        let detail = null
        try {
          detail = (await response.json()).detail
        } catch {
          // no JSON body
        }
        throw new Error(detail || `Cathy couldn't speak just now (${response.status}).`)
      }
      const url = URL.createObjectURL(await response.blob())
      await new Promise((resolve, reject) => {
        audio.onended = resolve
        audio.onerror = () => reject(new Error('The greeting could not be played.'))
        audio.src = url
        audio.play().catch(reject)
      })
      URL.revokeObjectURL(url)
    } catch (err) {
      if (!activeRef.current) return
      setError(err.message)
    }
    if (activeRef.current) setPhase('offering')
  }, [name, org, count])

  const ask = useCallback(async () => {
    setError(null)
    setPhase('connecting')
    // The mic first — and the probe's tracks stopped at once, since the SDK
    // opens its own stream and a leaked probe keeps the recording light on.
    try {
      const probe = await navigator.mediaDevices.getUserMedia({ audio: true })
      probe.getTracks().forEach((track) => track.stop())
    } catch {
      if (!activeRef.current) return
      setError('Cathy needs the microphone to hear you. Allow it and try again.')
      setPhase('offering')
      return
    }
    let session
    try {
      session = await api('/api/v1/cathy/session', { method: 'POST' })
    } catch (err) {
      if (!activeRef.current) return
      setError(err.detail || "Cathy isn't available just now. Please try again.")
      setPhase('offering')
      return
    }
    try {
      conversationRef.current = await Conversation.startSession({
        signedUrl: session.signed_url,
        connectionType: 'websocket',
        dynamicVariables: { user_name: name, org_name: org, invite_count: count, ...variables },
        onConnect: () => {
          if (activeRef.current) setPhase('connected')
        },
        onModeChange: ({ mode }) => {
          if (activeRef.current) setSpeaking(mode === 'speaking')
        },
        onDisconnect: (details) => {
          conversationRef.current = null
          if (!activeRef.current) return
          // A hang-up by her or by them is the end; a refused or dropped
          // connection is an error to read, with the offer still open.
          if (details?.reason === 'error') {
            const why = [details.message, details.closeReason].filter(Boolean).join(' · ')
            setError(why || 'The call was cut off. Please try again.')
            setPhase('offering')
          } else {
            setPhase('ended')
          }
        },
        onError: (message) => {
          if (activeRef.current) {
            setError(typeof message === 'string' ? message : 'Something went wrong on the call.')
          }
        },
      })
      if (activeRef.current) setPhase('connected')
    } catch {
      conversationRef.current = null
      if (!activeRef.current) return
      setError("Cathy couldn't connect. Check your connection and try again.")
      setPhase('offering')
    }
  }, [name, org, count, variables])

  const end = useCallback(async () => {
    try {
      await conversationRef.current?.endSession()
    } catch {
      conversationRef.current = null
      if (activeRef.current) setPhase('ended')
    }
  }, [])

  if (available !== true) return null

  const live = phase === 'connecting' || phase === 'connected'
  // The card's two lines: what she is doing, and what to do about it.
  const title = {
    idle: `Talk to ${agentName}`,
    greeting: `${agentName} is speaking…`,
    offering: 'Anything you want to ask?',
    connecting: 'Connecting…',
    connected: speaking ? `${agentName} is speaking` : 'Listening',
    ended: 'Thanks for talking',
  }[phase]
  const line = {
    idle: hint,
    greeting: 'Just listen for a moment.',
    offering: '',
    connecting: 'Allow the microphone if asked.',
    connected: 'Tap the mic when you’re done.',
    ended: 'Tap the mic to talk again.',
  }[phase]

  function handleMain() {
    if (phase === 'idle' || phase === 'ended') greet()
    else if (phase === 'connected') end()
  }

  return (
    <div className={`voice-card${live ? ' voice-card--on' : ''}`}>
      <div className="voice-card__row">
        <button
          type="button"
          className={`voice__btn${live || phase === 'greeting' ? ' voice__btn--on' : ''}`}
          onClick={handleMain}
          disabled={phase === 'greeting' || phase === 'connecting' || phase === 'offering'}
          aria-pressed={live}
          aria-label={phase === 'connected' ? `End the call with ${agentName}` : `Talk to ${agentName}`}
        >
          {live || phase === 'greeting' ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="9" y="2" width="6" height="12" rx="3" />
              <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
              <path d="M12 18v4" />
            </svg>
          )}
        </button>
        <div className="voice-card__text" aria-live="polite">
          <span className={`voice-card__title${live ? ' voice-card__title--on' : ''}`}>{title}</span>
          {line && <span className="voice-card__line">{line}</span>}
        </div>
      </div>
      {phase === 'offering' && (
        <div className="voice__offer">
          <button type="button" className="btn btn--primary btn--sm voice__ask" onClick={ask}>
            Ask {agentName} a question
          </button>
          <button
            type="button"
            className="btn btn--secondary btn--sm voice__no"
            onClick={() => setPhase('idle')}
          >
            No thanks
          </button>
        </div>
      )}
      {error && (
        <p className="voice__error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
